import React, { PropsWithChildren, ReactNode, useMemo, JSX, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { Button } from 'reactstrap';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import RentalCarOfficeWithDistanceWrapper from '../../../Models/RentalCarOfficeWithDistanceWrapper';
import fallback from '../../../_Translations/generated/DropdownFilter.en.t.json';
import Keys from '../../../_Translations/generated/DropdownFilter.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';

export type DropdownFilterComponent = { id: string; component: ReactNode };
export type Pill = { id: string; label: string; isClickable?: boolean; resetProp?: string };
type NestedListItem<T> = { id: number; list: T[] };

interface Props {
  filterComponents: Array<DropdownFilterComponent>;
  pills: Array<Pill>;
  filtersPerRow?: number;
  pillsPerRow?: number;
  onPillClick: (id: string) => void;
  showSearchButton?: boolean;
  onChange: (value: RentalCarOfficeWithDistanceWrapper[]) => void;
  selectedOptions: RentalCarOfficeWithDistanceWrapper[];
}

const DropdownFilter = (props: PropsWithChildren<Props>): JSX.Element => {
  const {
    filtersPerRow = 3,
    pillsPerRow = 3,
    filterComponents,
    pills,
    onPillClick,
    showSearchButton,
    onChange,
    selectedOptions
  } = props;

  const { t } = useTranslation('DropdownFilter', fallback, Keys);

  const filtersAvailableSpace = 100;

  const nestedFilterComponents = useMemo(() => {
    const nestedList: Array<NestedListItem<DropdownFilterComponent>> = [];
    let nestedIdx = -1;
    filterComponents.forEach((component: DropdownFilterComponent, idx: number) => {
      if (idx % filtersPerRow === 0) {
        ++nestedIdx;
        nestedList[nestedIdx] = { id: nestedIdx, list: [component] };
      } else {
        nestedList[nestedIdx] = {
          id: nestedList[nestedIdx].id,
          list: [...nestedList[nestedIdx].list, component]
        };
      }
    });
    return nestedList;
  }, [filterComponents]);

  const nestedPills = useMemo(() => {
    const nestedList: Array<NestedListItem<Pill>> = [];
    let nestedIdx = -1;
    pills.forEach((pill: Pill, idx: number) => {
      if (idx % pillsPerRow === 0) {
        ++nestedIdx;
        nestedList[nestedIdx] = { id: nestedIdx, list: [pill] };
      } else {
        nestedList[nestedIdx] = {
          id: nestedList[nestedIdx].id,
          list: [...nestedList[nestedIdx].list, pill]
        };
      }
    });
    return nestedList;
  }, [pills]);

  const getComponentStyle = useCallback(
    (component: DropdownFilterComponent): React.CSSProperties => {
      const defaultStyle = {
        flex: `0 0 ${filtersAvailableSpace / filtersPerRow}%`,
        maxWidth: `${filtersAvailableSpace / filtersPerRow}%`,
        position: 'relative'
      } as const;

      if (!showSearchButton) {
        return defaultStyle;
      }

      const componentId = parseInt(component.id, 10);
      if (componentId === 0 || componentId === 3) {
        return { flex: '1 0 30%', maxWidth: '30%', position: 'relative' };
      } else {
        return { flex: '1 0 20%', maxWidth: '20%', position: 'relative' };
      }
    },
    [filtersAvailableSpace, filtersPerRow, showSearchButton]
  );

  return (
    <div className="dropdown-filter">
      <div className="dropdown-filter__container">
        {nestedFilterComponents.map((item: NestedListItem<DropdownFilterComponent>) => (
          <div key={item.id} className="dropdown-filter__row">
            {item.list.map(component => {
              return (
                <div
                  key={component.id}
                  className="dropdown-filter__filter"
                  style={getComponentStyle(component)}
                >
                  {component.component}
                </div>
              );
            })}
            {showSearchButton && (
              <div className="dropdown-filter__search">
                <Button
                  color="primary"
                  type="submit"
                  onClick={e => {
                    e.currentTarget.blur();
                    onChange(selectedOptions);
                  }}
                >
                  <>
                    <FontAwesomeIcon icon={faSearch} className="mr-3" />
                    <span>{t(Keys.search)}</span>
                  </>
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="dropdown-filter__pills">
        {nestedPills.map((item: NestedListItem<Pill>) => (
          <div
            key={item.id}
            className={`dropdown-filter__row${
              item.list.length === pillsPerRow ? ' dropdown-filter__row--full' : ''
            }`}
          >
            {item.list.map(pill => (
              <div
                key={pill.id}
                className={`dropdown-filter__pill dropdown-filter__pill--${pill.id}`}
              >
                <div className="dropdown-filter__pill__inner">
                  <span>{pill.label}</span>
                  {pill.isClickable && (
                    <FontAwesomeIcon
                      icon={faTimesCircle}
                      onClick={(): void => onPillClick(pill.id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DropdownFilter;
